<script setup>
import { ref } from 'vue'
import { useRoute } from 'vue-router'
import { DxList } from 'devextreme-vue/list'

import { getI18n } from '@/plugins/i18n'
import { planningApi } from '@/apis/planning.api'
import { usePlanningStore } from '@/stores/planning.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import { useODataSource } from '@/composables/data-grid'
import { useChips } from '@/composables/chips'
import { useArchive } from '@/composables/archive'
import { useHighlightNewRow } from '@/composables/scroll'

import ListItem from '@/components/shared/ListItem.vue'
import ListingDetail from '@/components/shared/ListingDetail.vue'
import ListingEdit from '@/components/shared/ListingEdit.vue'
import FilterSortSelector from '@/components/shared/FilterSortSelector.vue'

const i18n = getI18n()
const route = useRoute()
const planningStore = usePlanningStore()
const webSiteStore = useWebSiteStore()
const { getListingChips } = useChips()
const { archivedChip } = useArchive()
const { highlightedRowId, highlightRowAsync } = useHighlightNewRow()
const isEmptyDataSource = ref(false)
const selectedItem = ref(null)
const editItem = ref(null)

const listContainer = ref(null)
let listInstance = null

const getCompanyAvatar = (listing) => {
  const company = planningStore.companies.find(c => c.id === listing.company)
  return company?.avatar_file || company?.avatar_url
}

webSiteStore.fabActions = [
  {
    id: 'add',
    icon: 'mdi-plus',
    onClick: () => { editItem.value = {} },
  },
]

const dataSource = useODataSource(planningApi.getAbsoluteUrl() + planningApi.listingsUrl, {
  onChanged: () => {
    isEmptyDataSource.value = dataSource.totalCount() === 0
  },
  searchExpr: 'search_field',
})

const getRandomNumber = () => (Math.floor(Math.random() * 100) % 5) + 1

const onChange = () => {
  selectedItem.value = null
  editItem.value = null
  dataSource.reload()
}

const onEditItemCreated = async () => {
  editItem.value = null
  webSiteStore.activateUpdatedDateSort(route.name)
  dataSource.sort(webSiteStore.buildSortOrderBy(route.name))
  dataSource.pageIndex(0)
  const loaded = await dataSource.reload()
  await highlightRowAsync(loaded?.[0]?.id, listContainer)
  listInstance?.repaint()
}

</script>
<template>
  <div>
    <filter-sort-selector :data-source="dataSource" />
    <q-card flat>
      <q-card-section ref="listContainer" class="q-pa-none">
        <q-dialog
          maximized
          :model-value="selectedItem !== null"
        >
          <listing-detail
            v-if="selectedItem !== null"
            :model-value="selectedItem"
            @cancel="selectedItem = null"
            @update="onChange"
          />
        </q-dialog>
        <q-dialog
          maximized
          :model-value="editItem !== null"
        >
          <listing-edit
            v-if="editItem !== null"
            :model-value="editItem"
            @update="onEditItemCreated"
            @cancel="onChange"
            @delete="onChange"
          />
        </q-dialog>
        
        <dx-list
          v-show="!isEmptyDataSource"
          :data-source="dataSource"
          height="calc(100vh - 8rem)"
          page-load-mode="scrollBottom"
          class="events-list"
          item-template="item"
          selection-mode="none"
          :active-state-enabled="false"
          @initialized="e => listInstance = e.component"
        >
          <template #item="{ data: availableItem }">
            <q-item>
              <list-item
                :key="availableItem.id"
                class="q-pa-sm"
                :class="{ 'row-highlight': availableItem.id === highlightedRowId }"
                :avatar="getCompanyAvatar(availableItem)"
                avatar-icon="mdi-office-building"
                :label="availableItem.name"
                :caption="availableItem.description"
                :caption-chips="[...getListingChips(availableItem),availableItem.archived ? archivedChip : null].filter(Boolean)"
                :disabled="availableItem.archived"
                dense
                clickable
                @click="selectedItem = availableItem"
              />
            </q-item>
          </template>
        </dx-list>
        <div
          v-show="isEmptyDataSource"
          class="empty-container"
        >
          <img
            src="/img/no-documents.svg"
            :style="{ maxWidth: '80vw', objectFit: 'contain' }"
            class="q-mb-xl"
          >
          <p class="text-h5">
            {{ i18n.t('common.noListing') }}
          </p>
          <p class="sub text-weight-light">
            {{ i18n.t(`common.noListing${getRandomNumber()}`) }}
          </p>
        </div>
      </q-card-section>
    </q-card>
  </div>
</template>
